import { useEffect, useMemo, useRef } from "react"

import { Spin } from "antd"
import {
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip
} from "chart.js"
import { Line } from "react-chartjs-2"
import { useSelector } from "react-redux"

import { useIncognito } from "../../../../hooks/useIncognito"
import { debounce } from "../../../../Utils/utils"
import { alertsChartOptions } from "../../../DashboardPage/components/chartsOptions"
import { getGradient, tooltipLine } from "../../../DashboardPage/components/chartsPlagins"

ChartJS.register(TimeScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler)

ChartJS.defaults.color = "#667085"
// ChartJS.defaults.font.family = 'FontAwesome, Inter, sans-serif';

ChartJS.defaults.font.family = "Inter, sans-serif"
ChartJS.defaults.plugins.tooltip.titleAlign = "center"
ChartJS.defaults.plugins.tooltip.padding = 12
ChartJS.defaults.plugins.tooltip.titleMarginBottom = 8
ChartJS.defaults.plugins.tooltip.titleFont = {
  fontFamily: "Inter, sans-serif",
  lineHeight: 1.2,
  size: 14,
  style: "normal",
  weight: "700"
}

const ReferralsOverviewChart = ({ data, isFetching }) => {
  const { overviewData, overviewDataFetching, filters } = useSelector((state) => state.referrals)

  const theme = useSelector((state) => state.nav.theme)
  const incognito = useIncognito()

  //for better resize chart if resize window
  const chartRef = useRef(null)

  const handleResize = debounce(() => {
    if (chartRef.current) {
      chartRef.current.resize()
    }
  }, 500)

  //for udate chart after changing browser tab if chart is unvisible
  const handleVisibilityChange = () => {
    if (document.visibilityState === "visible" && chartRef.current) {
      chartRef.current.update()
    }
  }

  useEffect(() => {
    window.addEventListener("resize", handleResize)
    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () => {
      window.removeEventListener("resize", handleResize)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [])

  const chartOptions = useMemo(() => alertsChartOptions(theme), [theme])

  const getChartData = (data) => ({
    labels: data?.labels,
    datasets: [
      {
        label: "All projects",
        data: data?.result?.daily_total,

        selectType: "count",

        total: data?.result?.daily_total,
        ethoca: data?.result?.alerts_ethoca,
        rdr: data?.result?.alerts_rdr,
        cdrn: data?.result?.alerts_cdrn,

        borderColor: "#225AEA",
        borderWidth: 2,
        pointHoverRadius: 8,
        pointHoverBorderWidth: 2,
        pointHoverBackgroundColor: "white",
        yAxisID: "y",
        fill: true,
        backgroundColor: function (context) {
          const chart = context.chart
          const { ctx, chartArea } = chart
          if (!chartArea) return
          return getGradient(ctx, chartArea)
        },
        ...(data?.labels?.length === 1
          ? { pointHoverRadius: 6, pointRadius: 4 }
          : { pointHoverRadius: 6, pointRadius: 0 })
      }
    ]
  })

  // No line/points/values to plot while masked - the chart frame stays, just empty.
  const chartData = incognito ? { labels: [], datasets: [] } : getChartData(data ?? overviewData)

  return (
    <Spin spinning={isFetching ?? overviewDataFetching}>
      <Line
        ref={chartRef}
        options={chartOptions}
        data={chartData}
        height={540}
        plugins={[tooltipLine]}
      />
    </Spin>
  )
}

export default ReferralsOverviewChart
